import {parseInteger} from '@benchling/js-utils';

import {Schema} from 'schemas/SchemaConstants';

import {DELETED_SCHEMA_ID, FormErrors, LookupStepInfo, LookupStepType} from './LookupConfigModalConstants';
import {getLookupStepInfoWithType, getLookupStepSchemaId, hasLookupStepContext} from './LookupConfigUtils';

function areRowsColsToIgnoreValid(rowsColsToIgnore: Array<string | number>): boolean {
  if (new Set(rowsColsToIgnore).size !== rowsColsToIgnore.length) {
    return false;
  }
  return rowsColsToIgnore.every((val) => typeof val === 'number' && Number.isInteger(val) && val >= 0);
}

function validateGridPosition(position: string): boolean {
  if (!position) {
    return false;
  }
  if ('A' <= position[0].toUpperCase() && position[0].toUpperCase() <= 'Z') {
    // coordinate format
    const splitPosition = position.match(/[a-zA-Z]+|[0-9]+/g);
    return splitPosition != null && splitPosition.length === 2 && parseInteger(splitPosition[1]) != null;
  } else {
    // number format
    return parseInteger(position) != null;
  }
}

function areWellsToIgnoreValid(wellsToIgnore: Array<string>): boolean {
  if (new Set(wellsToIgnore).size !== wellsToIgnore.length) {
    return false;
  }
  return wellsToIgnore.every((position) => validateGridPosition(position));
}

function isSelectTreeValid(lookupStepGroup: Array<LookupStepInfo>): boolean {
  const selectTreeSteps = lookupStepGroup.slice(1, lookupStepGroup.length);
  return selectTreeSteps.every((step) => !isStepMissingField(step));
}

function isStepMissingField(lookupStepInfo: LookupStepInfo): boolean {
  const lookupStep = lookupStepInfo.lookupStep;
  switch (lookupStep.type) {
    case LookupStepType.COUNT: {
      const schema = lookupStepInfo.parentSchema;
      if (schema == null) {
        return true;
      }
      const schemaField = schema.allFields.find((field) => field.apiId === lookupStep.schemaField);
      return schemaField == null;
    }
    case LookupStepType.FILTER:
    case LookupStepType.SCHEMA_FIELD:
      return lookupStepInfo.schemaField == null;
    case LookupStepType.FILTER_BY_STORED_WITHIN:
      return !lookupStep.value;
    case LookupStepType.CONTAINER_BY_GRID_POSITION:
      return lookupStep.schemaField == null;
    default:
      return false;
  }
}

function isStepMissingSecondaryField(lookupStepInfo: LookupStepInfo): boolean {
  const lookupStep = lookupStepInfo.lookupStep;
  switch (lookupStep.type) {
    case LookupStepType.CONTAINER_BY_GRID_POSITION:
      return lookupStep.gridPositionSchemaField == null;
    default:
      return false;
  }
}

function isStepMissingFormOption(lookupStepInfo: LookupStepInfo): boolean {
  const lookupStep = lookupStepInfo.lookupStep;
  const schemaId = getLookupStepSchemaId(lookupStepInfo);
  const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
  const schema = lookupStepInfoWithType?.linkSchema;
  switch (lookupStep.type) {
    case LookupStepType.COUNT:
      return isStepMissingField(lookupStepInfo);
    case LookupStepType.PLACEHOLDER_BOXES:
    case LookupStepType.PLACEHOLDER_PLATES:
      return schema == null;
    case LookupStepType.BOX:
    case LookupStepType.PLATE:
    case LookupStepType.CONTAINER:
    case LookupStepType.LOCATION:
      return schemaId === DELETED_SCHEMA_ID && schema == null;
    case LookupStepType.CONTENTS: {
      if (schemaId === DELETED_SCHEMA_ID && schema == null) {
        return true;
      }
      // Can't specify both entity schema and entity schema type
      return lookupStep.entitySchema != null && lookupStep.entitySchemaType != null;
    }
    default:
      return false;
  }
}

export default function validateLookupSteps(
  lookupStepGroup: Array<LookupStepInfo>,
  baseSchema: Schema | null,
  basePaneInvalidLookupObjectFn?: (
    lookupStepGroup: Array<LookupStepInfo>,
    baseSchema: Schema | null
  ) => boolean
): FormErrors {
  const errors: FormErrors = {};
  if (lookupStepGroup.length === 0) {
    return errors;
  }
  const firstStepInfo = lookupStepGroup[0];
  if (firstStepInfo.shouldSkipValidation) {
    return errors;
  }

  const firstStep = firstStepInfo.lookupStep;
  const filterLookupSteps = lookupStepGroup[lookupStepGroup.length - 1].filterLookupSteps;
  errors.selectDropdown = isStepMissingField(firstStepInfo);
  errors.dropdownValue = isStepMissingFormOption(firstStepInfo);
  errors.selectTreeError = !isSelectTreeValid(lookupStepGroup);
  errors.filter = filterLookupSteps ? !filterLookupSteps.every((step) => !isStepMissingField(step)) : false;

  switch (firstStep.type) {
    case LookupStepType.WELLS:
    case LookupStepType.BOX_CONTAINERS: {
      errors.rowsToIgnore = !areRowsColsToIgnoreValid(firstStep.filter.rowsToIgnore);
      errors.columnsToIgnore = !areRowsColsToIgnoreValid(firstStep.filter.columnsToIgnore);
      errors.wellsToIgnore = !areWellsToIgnoreValid(firstStep.filter.wellsToIgnore);
      break;
    }
    case LookupStepType.SOURCE:
    case LookupStepType.DESTINATION: {
      if (hasLookupStepContext(firstStepInfo)) {
        const lookupErrors = validateLookupSteps(
          firstStepInfo.lookupStepContext,
          baseSchema,
          basePaneInvalidLookupObjectFn
        );
        errors.selectDropdown = !isFormErrorsEmpty(lookupErrors);
      }
      break;
    }
    case LookupStepType.CONTAINER_BY_GRID_POSITION:
      errors.missingSecondaryField = isStepMissingSecondaryField(firstStepInfo);
      break;
    default:
      if (basePaneInvalidLookupObjectFn?.(lookupStepGroup, baseSchema)) {
        errors.invalidLookupObjectError = true;
      }
      break;
  }
  if (basePaneInvalidLookupObjectFn?.(lookupStepGroup, baseSchema)) {
    errors.invalidLookupObjectError = true;
  }
  errors.missingField = errors.selectDropdown || errors.selectTreeError || errors.filter;
  return errors;
}

export function isFormErrorsEmpty(formErrors: object): boolean {
  return Object.values(formErrors).every((value) => !value);
}
